// ─────────────────────────────────────────────────────────────────────────────
// MARKETING APPROVAL PORTAL — Pharmacy Innovations
// React + MSAL + Microsoft Graph (SharePoint List Integration)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useContext, createContext, useCallback } from "react";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import logoStacked from "./Logo Stacked.svg";
import logoLandscape from "./Logo Landscape.svg";

// ── MSAL CONFIG ─────────────────────────────────────────────────────────────
const msalConfig = {
  auth: {
    clientId: "0e97e8a9-1b89-4a10-9051-6eeb7a5f730e",
    authority: "https://login.microsoftonline.com/14c4fb10-085e-4ac6-a5b3-d49f57a97731",
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "localStorage", storeAuthStateInCookie: false },
};
const msalInstance = new PublicClientApplication(msalConfig);
const graphScopes = ["User.Read", "User.ReadBasic.All", "Sites.ReadWrite.All"];

// ── SharePoint Config ───────────────────────────────────────────────────────
// Approval Queue (Creative & Digital Strategy site)
const SP_SITE_ID = "pharmacyinnovationsnet.sharepoint.com:/sites/CreativeDigitalStrategy";
const SP_LIST_NAME = "Content Scheduler";
// Material Requests (Marketing site)
const SP_MAT_SITE_ID = "pharmacyinnovationsnet.sharepoint.com:/sites/msteams_01d628_311240";
const SP_MAT_LIST_NAME = "New Material Request";

// ── COLORS ──────────────────────────────────────────────────────────────────
const colors = {
  primary: "#1e3a5f",
  primaryDark: "#172a4f",
  primaryLight: "#2d5a9e",
  lightBg: "#eef3fb",
  accent: "#3b82f6",
  white: "#ffffff",
  gray: "#64748b",
  danger: "#ef4444",
  success: "#10b981",
  warning: "#f59e0b",
};

// ── CONTEXTS ────────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// ── STYLES ──────────────────────────────────────────────────────────────────
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "DM Sans,sans-serif", outline: "none", background: "#fff", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontFamily: "DM Sans,sans-serif" };
const textareaStyle = { ...inputStyle, minHeight: 80, resize: "vertical" };

// ── ADMIN / APPROVER CONFIG ─────────────────────────────────────────────────
const ADMIN_EMAILS = [
  "abrown@pharmacyinnovations.net",
  "jbarlekoff@pharmacyinnovations.net",
  "swilliams@pharmacyinnovations.net",
];
const CONTENT_APPROVERS = [
  "abrown@pharmacyinnovations.net",
  "swilliams@pharmacyinnovations.net",
];
const DESIGN_APPROVERS = [
  "abrown@pharmacyinnovations.net",
  "jbarlekoff@pharmacyinnovations.net",
];

// ── HELPERS ─────────────────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getDate()).padStart(2, "0")}/${dt.getFullYear()}`;
  } catch { return d; }
};

const timeAgo = (d) => {
  if (!d) return "";
  const now = new Date();
  const then = new Date(d);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmt(d);
};

// ── ICONS ───────────────────────────────────────────────────────────────────
const Ic = {
  add: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  x: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  clock: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  chart: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
  file: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  send: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
  inbox: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>,
  eye: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  link: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
  image: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>,
  logout: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>,
  edit: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  back: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
  upload: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
  filter: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  refresh: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  package: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
  arrowRight: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
};

// ── REUSABLE COMPONENTS ─────────────────────────────────────────────────────
function Btn({ children, onClick, disabled, small, outline, danger, success: successProp, style: extra, ...rest }) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: small ? "6px 12px" : "10px 18px",
    border: outline ? `1.5px solid ${danger ? colors.danger : colors.primary}` : "none",
    borderRadius: 8,
    background: outline ? "transparent" : danger ? colors.danger : successProp ? colors.success : colors.primary,
    color: outline ? (danger ? colors.danger : colors.primary) : "#fff",
    fontSize: small ? 12 : 13, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "DM Sans,sans-serif", opacity: disabled ? 0.5 : 1, transition: "all 0.15s",
    ...extra,
  };
  return <button style={base} onClick={disabled ? undefined : onClick} disabled={disabled} {...rest}>{children}</button>;
}

function Badge({ label, color, bg }) {
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: color || "#fff", background: bg || colors.primary, fontFamily: "DM Sans,sans-serif", whiteSpace: "nowrap" }}>{label}</span>;
}

function StatCard({ label, value, color, bg, icon, sub }) {
  return (
    <div style={{ background: bg || "#f0f9ff", borderRadius: 12, padding: "16px 18px", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "DM Sans,sans-serif" }}>{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || colors.primary, lineHeight: 1, fontFamily: "DM Sans,sans-serif" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#64748b", fontFamily: "DM Sans,sans-serif", marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "28px 32px", maxWidth: wide ? 700 : 520, width: "100%", maxHeight: "85vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 18, fontWeight: 800, color: "#0f172a", margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 20 }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  const bg = type === "error" ? "#fef2f2" : type === "success" ? "#f0fdf4" : "#eff6ff";
  const border = type === "error" ? "#fca5a5" : type === "success" ? "#86efac" : "#93c5fd";
  const color = type === "error" ? "#b91c1c" : type === "success" ? "#15803d" : "#1e40af";
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: "12px 20px", fontSize: 13, color, fontFamily: "DM Sans,sans-serif", fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", zIndex: 10000, maxWidth: 400 }}>
      {msg}
    </div>
  );
}

function Avatar({ name, size = 32, idx = 0 }) {
  const bgColors = ["#1e3a5f", "#2d5a9e", "#7c3aed", "#059669", "#d97706", "#dc2626", "#0891b2", "#be185d"];
  const initials = (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bgColors[idx % bgColors.length], display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: size * 0.38, fontWeight: 700, fontFamily: "DM Sans,sans-serif", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

// ── GRAPH API HELPERS ───────────────────────────────────────────────────────
async function getToken(instance, accounts) {
  try {
    const resp = await instance.acquireTokenSilent({ scopes: graphScopes, account: accounts[0] });
    return resp.accessToken;
  } catch {
    const resp = await instance.acquireTokenPopup({ scopes: graphScopes });
    return resp.accessToken;
  }
}

async function graphGet(token, url) {
  const resp = await fetch(`https://graph.microsoft.com/v1.0${url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Graph API ${resp.status}: ${resp.statusText}`);
  return resp.json();
}

async function graphPost(token, url, body) {
  const resp = await fetch(`https://graph.microsoft.com/v1.0${url}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Graph API ${resp.status}: ${err}`);
  }
  return resp.json();
}

async function graphPatch(token, url, body) {
  const resp = await fetch(`https://graph.microsoft.com/v1.0${url}`, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Graph API ${resp.status}: ${err}`);
  }
  return resp.json();
}

// ── SharePoint List API ─────────────────────────────────────────────────────
async function getSiteId(token) {
  const data = await graphGet(token, `/sites/${SP_SITE_ID}`);
  return data.id;
}

async function getListId(token, siteId) {
  const data = await graphGet(token, `/sites/${siteId}/lists?$filter=displayName eq '${SP_LIST_NAME}'`);
  if (data.value.length === 0) throw new Error("List not found: " + SP_LIST_NAME);
  return data.value[0].id;
}

async function getListItems(token, siteId, listId) {
  let allItems = [];
  let url = `/sites/${siteId}/lists/${listId}/items?$expand=fields&$top=100`;
  while (url) {
    const data = await graphGet(token, url);
    allItems = allItems.concat(data.value || []);
    url = data["@odata.nextLink"] ? data["@odata.nextLink"].replace("https://graph.microsoft.com/v1.0", "") : null;
  }
  return allItems;
}

async function createListItem(token, siteId, listId, fields) {
  return graphPost(token, `/sites/${siteId}/lists/${listId}/items`, { fields });
}

async function updateListItem(token, siteId, listId, itemId, fields) {
  return graphPatch(token, `/sites/${siteId}/lists/${listId}/items/${itemId}/fields`, fields);
}

// ── APPROVAL TYPE HELPERS ───────────────────────────────────────────────────
function getApprovalBadge(type) {
  if (type === "Design") return { color: "#7c3aed", bg: "#f3e8ff" };
  return { color: "#0369a1", bg: "#e0f2fe" }; // Content
}

function getStatusBadge(status) {
  switch (status) {
    case "Approved": return { color: "#15803d", bg: "#f0fdf4" };
    case "Rejected": return { color: "#dc2626", bg: "#fef2f2" };
    case "Pending": return { color: "#d97706", bg: "#fffbeb" };
    default: return { color: "#64748b", bg: "#f1f5f9" }; // Not submitted
  }
}

// ── LOGIN SCREEN ────────────────────────────────────────────────────────────
function LoginScreen() {
  const { instance } = useMsal();
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#1e3a5f 0%,#2d5a9e 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: "48px 40px", maxWidth: 420, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <img src={logoStacked} alt="Pharmacy Innovations" style={{ height: 140, width: "auto", maxWidth: "80%", marginBottom: 24, display: "block", margin: "0 auto 24px auto" }} />
        <h1 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Marketing Approval Portal</h1>
        <p style={{ fontFamily: "DM Sans,sans-serif", fontSize: 13, color: "#64748b", marginBottom: 32 }}>Submit and manage content & design approval requests</p>
        <button onClick={() => instance.loginRedirect({ scopes: graphScopes })}
          style={{ width: "100%", padding: "13px 0", background: "#0078d4", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <svg width="20" height="20" viewBox="0 0 21 21" fill="none"><rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" /><rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" /></svg>
          Sign in with Microsoft 365
        </button>
      </div>
    </div>
  );
}

// ── NEW REQUEST FORM ────────────────────────────────────────────────────────
function NewRequestForm({ onSubmit, onClose, loading, prefill }) {
  const [form, setForm] = useState({
    Title: prefill?.Title || "",
    Description: prefill?.Description || "",
    Type_x0020_of_x0020_Approval: prefill?.Type_x0020_of_x0020_Approval || "Content",
    Content_x002f_Design_x0020_type: prefill?.Content_x002f_Design_x0020_type || "",
    Content_x002f_Collateral_x0020_Link: prefill?.["Content_x002f_Collateral_x0020_Link"] || "",
    Planner_x002f_Task_x0020_Link: prefill?.["Planner_x002f_Task_x0020_Link"] || "",
    Draft_x0020_due_x0020_by: prefill?.Draft_x0020_due_x0020_by || "",
    Publish_x0020_by: prefill?.Publish_x0020_by || "",
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <Modal title="New Approval Request" onClose={onClose} wide>
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <label style={labelStyle}>Approval Title *</label>
          <input style={inputStyle} value={form.Title} onChange={e => set("Title", e.target.value)} placeholder="e.g., June Sales Flyer" />
        </div>

        <div>
          <label style={labelStyle}>Description *</label>
          <textarea style={textareaStyle} value={form.Description} onChange={e => set("Description", e.target.value)} placeholder="Describe what needs to be approved..." />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Type of Approval *</label>
            <select style={inputStyle} value={form.Type_x0020_of_x0020_Approval} onChange={e => set("Type_x0020_of_x0020_Approval", e.target.value)}>
              <option value="Content">Content</option>
              <option value="Design">Design</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Content/Design Type</label>
            <select style={inputStyle} value={form.Content_x002f_Design_x0020_type} onChange={e => set("Content_x002f_Design_x0020_type", e.target.value)}>
              <option value="">— Select —</option>
              <option value="Collateral">Collateral</option>
              <option value="Social Media">Social Media</option>
              <option value="Website">Website</option>
              <option value="Email/Newsletter">Email/Newsletter</option>
              <option value="Print">Print</option>
              <option value="Blog">Blog</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Content/Collateral Link</label>
          <input style={inputStyle} value={form["Content_x002f_Collateral_x0020_Link"]} onChange={e => set("Content_x002f_Collateral_x0020_Link", e.target.value)} placeholder="https://..." />
        </div>

        <div>
          <label style={labelStyle}>Planner/Task Link</label>
          <input style={inputStyle} value={form["Planner_x002f_Task_x0020_Link"]} onChange={e => set("Planner_x002f_Task_x0020_Link", e.target.value)} placeholder="https://..." />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Draft Due By</label>
            <input type="date" style={inputStyle} value={form.Draft_x0020_due_x0020_by} onChange={e => set("Draft_x0020_due_x0020_by", e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Publish By</label>
            <input type="date" style={inputStyle} value={form.Publish_x0020_by} onChange={e => set("Publish_x0020_by", e.target.value)} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
          <Btn outline onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSubmit(form)} disabled={loading || !form.Title || !form.Description}>
            {loading ? "Submitting..." : <>{Ic.send} Submit Request</>}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

// ── ITEM DETAIL VIEW ────────────────────────────────────────────────────────
function ItemDetail({ item, onClose, onApprove, onReject, isApprover, loading }) {
  const f = item.fields || {};
  const approvalType = f.Type_x0020_of_x0020_Approval || "Content";
  const approvalBadge = getApprovalBadge(approvalType);
  const status = f.Approval_x0020_status || "Not submitted";
  const statusBadge = getStatusBadge(status);

  return (
    <Modal title={f.Title || "Request Detail"} onClose={onClose} wide>
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <Badge label={approvalType} color={approvalBadge.color} bg={approvalBadge.bg} />
        <Badge label={status} color={statusBadge.color} bg={statusBadge.bg} />
        {f.Content_x002f_Design_x0020_type && <Badge label={f.Content_x002f_Design_x0020_type} color="#475569" bg="#f1f5f9" />}
      </div>

      <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
        <div>
          <label style={labelStyle}>Description</label>
          <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif", lineHeight: 1.6, background: "#f8fafc", padding: "12px 16px", borderRadius: 8 }}>
            {f.Description || "No description provided"}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Created By</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{f.Author?.[0]?.LookupValue || f.Created_x0020_By || "—"}</div>
          </div>
          <div>
            <label style={labelStyle}>Created</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{fmt(f.Created || item.createdDateTime)}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Draft Due By</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{fmt(f.Draft_x0020_due_x0020_by)}</div>
          </div>
          <div>
            <label style={labelStyle}>Publish By</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{fmt(f.Publish_x0020_by)}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Publish Status</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{f.Publish_x0020_status || "—"}</div>
          </div>
          <div>
            <label style={labelStyle}>Sign-off Status</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{f.Sign_x002d_off_x0020_status || "—"}</div>
          </div>
        </div>

        {f["Content_x002f_Collateral_x0020_Link"] && (
          <div>
            <label style={labelStyle}>Content/Collateral Link</label>
            <a href={f["Content_x002f_Collateral_x0020_Link"]} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: colors.accent, fontFamily: "DM Sans,sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {Ic.link} Open Link
            </a>
          </div>
        )}

        {f["Planner_x002f_Task_x0020_Link"] && (
          <div>
            <label style={labelStyle}>Planner/Task Link</label>
            <a href={f["Planner_x002f_Task_x0020_Link"]} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: colors.accent, fontFamily: "DM Sans,sans-serif", display: "inline-flex", alignItems: "center", gap: 4 }}>
              {Ic.link} Open in Planner
            </a>
          </div>
        )}
      </div>

      {isApprover && status !== "Approved" && status !== "Rejected" && (
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn danger onClick={() => onReject(item)} disabled={loading}>
            {Ic.x} Reject
          </Btn>
          <Btn success onClick={() => onApprove(item)} disabled={loading}>
            {Ic.check} Approve
          </Btn>
        </div>
      )}
    </Modal>
  );
}

// ── REQUEST LIST TABLE ──────────────────────────────────────────────────────
function RequestTable({ items, onView, emptyMsg }) {
  if (!items.length) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: "#94a3b8", fontFamily: "DM Sans,sans-serif" }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>{Ic.inbox}</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{emptyMsg || "No requests found"}</div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "DM Sans,sans-serif", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Title</th>
            <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Type</th>
            <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Status</th>
            <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Created By</th>
            <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Created</th>
            <th style={{ padding: "10px 14px", width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const f = item.fields || {};
            const approvalType = f.Type_x0020_of_x0020_Approval || "—";
            const approvalBadge = getApprovalBadge(approvalType);
            const status = f.Approval_x0020_status || "Not submitted";
            const statusBadge = getStatusBadge(status);
            const createdBy = f.Author?.[0]?.LookupValue || f.Created_x0020_By || "—";
            return (
              <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background 0.1s" }}
                onClick={() => onView(item)}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "12px 14px", fontWeight: 600, color: "#0f172a", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {f.Title || "Untitled"}
                </td>
                <td style={{ padding: "12px 14px" }}>
                  <Badge label={approvalType} color={approvalBadge.color} bg={approvalBadge.bg} />
                </td>
                <td style={{ padding: "12px 14px" }}>
                  <Badge label={status} color={statusBadge.color} bg={statusBadge.bg} />
                </td>
                <td style={{ padding: "12px 14px", color: "#64748b" }}>{createdBy}</td>
                <td style={{ padding: "12px 14px", color: "#64748b" }}>{timeAgo(f.Created || item.createdDateTime)}</td>
                <td style={{ padding: "12px 14px", color: "#94a3b8" }}>{Ic.eye}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── MATERIAL REQUEST STATUS HELPERS ──────────────────────────────────────────
function getCreationStatusBadge(status) {
  switch (status) {
    case "New Request": return { color: "#0369a1", bg: "#e0f2fe" };
    case "Request in Review": return { color: "#7c3aed", bg: "#f3e8ff" };
    case "Waiting for Info": return { color: "#d97706", bg: "#fffbeb" };
    case "In Process": return { color: "#0891b2", bg: "#ecfeff" };
    case "Waiting for Approval": return { color: "#ea580c", bg: "#fff7ed" };
    case "Complete": return { color: "#15803d", bg: "#f0fdf4" };
    case "Cancelled": return { color: "#64748b", bg: "#f1f5f9" };
    default: return { color: "#64748b", bg: "#f1f5f9" };
  }
}

function getPriorityBadge(priority) {
  switch (priority) {
    case "High": return { color: "#dc2626", bg: "#fef2f2" };
    case "Urgent": return { color: "#fff", bg: "#dc2626" };
    default: return { color: "#64748b", bg: "#f1f5f9" }; // Normal
  }
}

// ── MATERIAL REQUEST CARD (Kanban-style) ────────────────────────────────────
function MaterialRequestCard({ item, onClick, onCreateApproval }) {
  const f = item.fields || {};
  const statusBadge = getCreationStatusBadge(f.Creation_x0020_Status || "");
  const priorityBadge = getPriorityBadge(f.Priority_x0020_Level || "Normal");
  const isActionable = f.Creation_x0020_Status !== "Complete" && f.Creation_x0020_Status !== "Cancelled";

  return (
    <div onClick={() => onClick(item)} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px", cursor: "pointer", transition: "all 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = "#e2e8f0"; }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", fontFamily: "DM Sans,sans-serif", marginBottom: 8, lineHeight: 1.3 }}>{f.Title || "Untitled"}</div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        <Badge label={f.Creation_x0020_Status || "—"} color={statusBadge.color} bg={statusBadge.bg} />
        {f.Priority_x0020_Level && f.Priority_x0020_Level !== "Normal" && <Badge label={f.Priority_x0020_Level} color={priorityBadge.color} bg={priorityBadge.bg} />}
      </div>
      {f.Material_x0020_Type && <div style={{ fontSize: 11, color: "#64748b", fontFamily: "DM Sans,sans-serif", marginBottom: 4 }}>{f.Material_x0020_Type}</div>}
      {f.Intended_x0020_Audience && <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "DM Sans,sans-serif", marginBottom: 8 }}>{f.Intended_x0020_Audience}</div>}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: "DM Sans,sans-serif" }}>{timeAgo(item.createdDateTime)}</span>
        {isActionable && onCreateApproval && (
          <button onClick={e => { e.stopPropagation(); onCreateApproval(item); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", border: "1px solid #3b82f6", borderRadius: 6, background: "transparent", color: "#3b82f6", fontSize: 10, fontWeight: 700, cursor: "pointer", fontFamily: "DM Sans,sans-serif" }}>
            {Ic.send} Create Approval
          </button>
        )}
      </div>
    </div>
  );
}

// ── MATERIAL REQUEST DETAIL ─────────────────────────────────────────────────
function MaterialRequestDetail({ item, onClose, onCreateApproval }) {
  const f = item.fields || {};
  const statusBadge = getCreationStatusBadge(f.Creation_x0020_Status || "");
  const isActionable = f.Creation_x0020_Status !== "Complete" && f.Creation_x0020_Status !== "Cancelled";

  return (
    <Modal title={f.Title || "Material Request"} onClose={onClose} wide>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        <Badge label={f.Creation_x0020_Status || "—"} color={statusBadge.color} bg={statusBadge.bg} />
        {f.Priority_x0020_Level && <Badge label={`Priority: ${f.Priority_x0020_Level}`} color={getPriorityBadge(f.Priority_x0020_Level).color} bg={getPriorityBadge(f.Priority_x0020_Level).bg} />}
        {f.Material_x0020_Type && <Badge label={f.Material_x0020_Type} color="#475569" bg="#f1f5f9" />}
      </div>

      <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
        {f.Intended_x0020_Audience && (
          <div>
            <label style={labelStyle}>Intended Audience</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{f.Intended_x0020_Audience}</div>
          </div>
        )}

        {f.Description_x0020_of_x0020_Material && (
          <div>
            <label style={labelStyle}>Description of Material</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif", lineHeight: 1.6, background: "#f8fafc", padding: "12px 16px", borderRadius: 8, whiteSpace: "pre-wrap" }}>
              {f.Description_x0020_of_x0020_Material}
            </div>
          </div>
        )}

        {f.Additional_x0020_Notes && (
          <div>
            <label style={labelStyle}>Additional Notes</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif", lineHeight: 1.6, background: "#f8fafc", padding: "12px 16px", borderRadius: 8, whiteSpace: "pre-wrap" }}>
              {f.Additional_x0020_Notes}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Requested By</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{f.Author?.[0]?.LookupValue || "—"}</div>
          </div>
          <div>
            <label style={labelStyle}>Created</label>
            <div style={{ fontSize: 13, color: "#374151", fontFamily: "DM Sans,sans-serif" }}>{fmt(item.createdDateTime)}</div>
          </div>
        </div>
      </div>

      {isActionable && onCreateApproval && (
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn onClick={() => { onCreateApproval(item); onClose(); }}>
            {Ic.send} Create Approval Request from This
          </Btn>
        </div>
      )}
    </Modal>
  );
}

// ── MATERIAL REQUESTS SECTION ───────────────────────────────────────────────
function MaterialRequestsSection({ matItems, approvalItems, onCreateApproval, loading }) {
  const [selectedMat, setSelectedMat] = useState(null);

  // Group by creation status for kanban-like display
  const statusOrder = ["New Request", "Request in Review", "Waiting for Info", "In Process", "Waiting for Approval"];
  const activeItems = matItems.filter(i => {
    const s = i.fields?.Creation_x0020_Status || "";
    return s !== "Complete" && s !== "Cancelled";
  });

  const grouped = {};
  statusOrder.forEach(s => { grouped[s] = []; });
  activeItems.forEach(item => {
    const s = item.fields?.Creation_x0020_Status || "New Request";
    if (grouped[s]) grouped[s].push(item);
    else grouped["New Request"].push(item);
  });

  return (
    <div>
      {/* Material Requests Kanban */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <h3 style={{ fontFamily: "DM Sans,sans-serif", fontWeight: 700, fontSize: 16, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
              {Ic.package} Material Requests
            </h3>
            <p style={{ fontSize: 11, color: "#64748b", fontFamily: "DM Sans,sans-serif", marginTop: 2 }}>
              {activeItems.length} active request{activeItems.length !== 1 ? "s" : ""} from employees
            </p>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontFamily: "DM Sans,sans-serif" }}>Loading material requests...</div>
        ) : activeItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontFamily: "DM Sans,sans-serif", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0" }}>
            No active material requests
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${statusOrder.length}, 1fr)`, gap: 12, overflowX: "auto" }}>
            {statusOrder.map(status => {
              const badge = getCreationStatusBadge(status);
              return (
                <div key={status} style={{ minWidth: 180 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, padding: "0 4px" }}>
                    <Badge label={status} color={badge.color} bg={badge.bg} />
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, fontFamily: "DM Sans,sans-serif" }}>({grouped[status].length})</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {grouped[status].map(item => (
                      <MaterialRequestCard key={item.id} item={item} onClick={setSelectedMat} onCreateApproval={onCreateApproval} />
                    ))}
                    {grouped[status].length === 0 && (
                      <div style={{ padding: "20px 12px", textAlign: "center", fontSize: 11, color: "#cbd5e1", fontFamily: "DM Sans,sans-serif", background: "#f8fafc", borderRadius: 8, border: "1px dashed #e2e8f0" }}>
                        No items
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedMat && <MaterialRequestDetail item={selectedMat} onClose={() => setSelectedMat(null)} onCreateApproval={onCreateApproval} />}
    </div>
  );
}

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────────
function Dashboard() {
  const { instance, accounts } = useMsal();
  const { currentUser } = useAuth();
  const [tab, setTab] = useState("overview");
  const [items, setItems] = useState([]);
  const [matItems, setMatItems] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [listId, setListId] = useState(null);
  const [matSiteId, setMatSiteId] = useState(null);
  const [matListId, setMatListId] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingMat, setLoadingMat] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFormPrefill, setNewFormPrefill] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");

  const email = currentUser?.email?.toLowerCase() || "";
  const isAdmin = ADMIN_EMAILS.includes(email);
  const isContentApprover = CONTENT_APPROVERS.includes(email);
  const isDesignApprover = DESIGN_APPROVERS.includes(email);
  const isApprover = isContentApprover || isDesignApprover;

  const loadData = useCallback(async () => {
    try {
      setLoadingData(true);
      const token = await getToken(instance, accounts);
      const sid = await getSiteId(token);
      setSiteId(sid);
      const lid = await getListId(token, sid);
      setListId(lid);
      const data = await getListItems(token, sid, lid);
      data.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));
      setItems(data);
    } catch (err) {
      console.error("Failed to load approval data:", err);
      setToast({ msg: "Failed to load approvals: " + err.message, type: "error" });
    } finally {
      setLoadingData(false);
    }
  }, [instance, accounts]);

  const loadMatData = useCallback(async () => {
    try {
      setLoadingMat(true);
      const token = await getToken(instance, accounts);
      const msid = await graphGet(token, `/sites/${SP_MAT_SITE_ID}`).then(d => d.id);
      setMatSiteId(msid);
      const mlid = await graphGet(token, `/sites/${msid}/lists?$filter=displayName eq '${SP_MAT_LIST_NAME}'`).then(d => {
        if (d.value.length === 0) throw new Error("Material Request list not found");
        return d.value[0].id;
      });
      setMatListId(mlid);
      const data = await getListItems(token, msid, mlid);
      data.sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));
      setMatItems(data);
    } catch (err) {
      console.error("Failed to load material requests:", err);
      setToast({ msg: "Failed to load material requests: " + err.message, type: "error" });
    } finally {
      setLoadingMat(false);
    }
  }, [instance, accounts]);

  useEffect(() => { loadData(); loadMatData(); }, [loadData, loadMatData]);

  // Filter items
  const myItems = items.filter(i => {
    const author = i.fields?.Author?.[0]?.Email?.toLowerCase() || "";
    return author === email;
  });

  const pendingApprovals = items.filter(i => {
    const f = i.fields || {};
    const status = f.Approval_x0020_status || "Not submitted";
    const type = f.Type_x0020_of_x0020_Approval || "";
    if (status === "Approved" || status === "Rejected") return false;
    if (type === "Content" && isContentApprover) return true;
    if (type === "Design" && isDesignApprover) return true;
    return false;
  });

  const approvedItems = items.filter(i => (i.fields?.Approval_x0020_status || "") === "Approved");
  const rejectedItems = items.filter(i => (i.fields?.Approval_x0020_status || "") === "Rejected");

  const filteredItems = filter === "all" ? items
    : filter === "content" ? items.filter(i => i.fields?.Type_x0020_of_x0020_Approval === "Content")
    : filter === "design" ? items.filter(i => i.fields?.Type_x0020_of_x0020_Approval === "Design")
    : filter === "approved" ? approvedItems
    : filter === "rejected" ? rejectedItems
    : filter === "pending" ? items.filter(i => {
      const s = i.fields?.Approval_x0020_status || "Not submitted";
      return s !== "Approved" && s !== "Rejected";
    })
    : items;

  async function handleSubmit(formData) {
    try {
      setLoadingAction(true);
      const token = await getToken(instance, accounts);
      const fields = { ...formData };
      // Clean empty fields
      Object.keys(fields).forEach(k => { if (!fields[k]) delete fields[k]; });
      await createListItem(token, siteId, listId, fields);
      setShowNewForm(false);
      setNewFormPrefill(null);
      setToast({ msg: "Request submitted successfully!", type: "success" });
      await loadData();
    } catch (err) {
      console.error("Submit failed:", err);
      setToast({ msg: "Failed to submit: " + err.message, type: "error" });
    } finally {
      setLoadingAction(false);
    }
  }

  function handleCreateApprovalFromMaterial(matItem) {
    const f = matItem.fields || {};
    setNewFormPrefill({
      Title: f.Title || "",
      Description: `Material Request: ${f.Title || ""}\n\n${f.Description_x0020_of_x0020_Material || ""}\n\n${f.Additional_x0020_Notes ? "Notes: " + f.Additional_x0020_Notes : ""}`.trim(),
      Type_x0020_of_x0020_Approval: "Design",
      Content_x002f_Design_x0020_type: f.Material_x0020_Type || "",
    });
    setShowNewForm(true);
  }

  async function handleApprove(item) {
    try {
      setLoadingAction(true);
      const token = await getToken(instance, accounts);
      await updateListItem(token, siteId, listId, item.id, {
        Approval_x0020_status: "Approved",
        Sign_x002d_off_x0020_status: `Approved by ${currentUser?.name} on ${new Date().toLocaleDateString()}`,
      });
      setSelectedItem(null);
      setToast({ msg: "Request approved!", type: "success" });
      await loadData();
    } catch (err) {
      setToast({ msg: "Failed to approve: " + err.message, type: "error" });
    } finally {
      setLoadingAction(false);
    }
  }

  async function handleReject(item) {
    try {
      setLoadingAction(true);
      const token = await getToken(instance, accounts);
      await updateListItem(token, siteId, listId, item.id, {
        Approval_x0020_status: "Rejected",
        Sign_x002d_off_x0020_status: `Rejected by ${currentUser?.name} on ${new Date().toLocaleDateString()}`,
      });
      setSelectedItem(null);
      setToast({ msg: "Request rejected", type: "error" });
      await loadData();
    } catch (err) {
      setToast({ msg: "Failed to reject: " + err.message, type: "error" });
    } finally {
      setLoadingAction(false);
    }
  }

  function handleLogout() {
    instance.logoutRedirect();
  }

  const activeMatItems = matItems.filter(i => { const s = i.fields?.Creation_x0020_Status || ""; return s !== "Complete" && s !== "Cancelled"; });

  const TABS = [
    { key: "overview", label: "Dashboard", icon: Ic.chart },
    { key: "materials", label: `Material Requests (${activeMatItems.length})`, icon: Ic.package },
    { key: "all", label: `Approval Queue (${items.length})`, icon: Ic.file },
    ...(isApprover ? [{ key: "approvals", label: `Approvals${pendingApprovals.length > 0 ? ` (${pendingApprovals.length})` : ""}`, icon: Ic.check }] : []),
    { key: "my", label: `My Submissions (${myItems.length})`, icon: Ic.send },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9" }}>
      {/* Top Header Bar */}
      <div style={{ background: colors.primary, padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={logoLandscape} alt="Pharmacy Innovations" style={{ height: 32, filter: "brightness(0) invert(1)" }} />
          <span style={{ color: "#fff", fontFamily: "DM Sans,sans-serif", fontWeight: 800, fontSize: 16 }}>Marketing Approval Portal</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Btn small onClick={() => setShowNewForm(true)} style={{ background: colors.accent }}>
            {Ic.add} New Request
          </Btn>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar name={currentUser?.name} size={30} idx={0} />
            <span style={{ color: "#cbd5e1", fontSize: 12, fontFamily: "DM Sans,sans-serif" }}>{currentUser?.name}</span>
          </div>
          <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer", color: "#94a3b8", display: "flex", alignItems: "center" }}>
            {Ic.logout}
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 24px", display: "flex", alignItems: "center", gap: 2, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "13px 14px", border: "none", borderBottom: `2px solid ${tab === t.key ? colors.primary : "transparent"}`, background: "transparent", color: tab === t.key ? colors.primary : "#64748b", fontWeight: tab === t.key ? 700 : 500, fontSize: 13, fontFamily: "DM Sans,sans-serif", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}>
            {t.icon}{t.label}
          </button>
        ))}
        <div style={{ marginLeft: "auto", padding: "8px 0" }}>
          <Btn small outline onClick={() => { loadData(); loadMatData(); }} disabled={loadingData || loadingMat} style={{ borderColor: "#e2e8f0", color: "#64748b" }}>
            {Ic.refresh} {loadingData || loadingMat ? "Loading..." : "Refresh"}
          </Btn>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: "0 auto", padding: "20px 16px" }}>
        {loadingData && items.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#94a3b8", fontFamily: "DM Sans,sans-serif" }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Loading requests from SharePoint...</div>
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {tab === "overview" && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <h2 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
                    {isAdmin ? "Marketing Approval Dashboard" : "My Dashboard"}
                  </h2>
                  <p style={{ fontSize: 12, color: "#64748b", fontFamily: "DM Sans,sans-serif", marginTop: 2 }}>
                    Content & design approval overview · {fmt(new Date().toISOString().split("T")[0])}
                  </p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }}>
                  <StatCard label="Material Requests" value={activeMatItems.length} color="#7c3aed" bg="#f3e8ff" icon="📦" sub={`${matItems.filter(i => i.fields?.Creation_x0020_Status === "New Request").length} new incoming`} />
                  <StatCard label="Approval Queue" value={items.length} color={colors.primary} icon="📋" sub={`${myItems.length} submitted by you`} />
                  <StatCard label="Pending Approval" value={items.filter(i => { const s = i.fields?.Approval_x0020_status || ""; return s !== "Approved" && s !== "Rejected"; }).length} color={colors.warning} bg="#fffbeb" icon="⏳" sub={isApprover ? `${pendingApprovals.length} need your review` : "Awaiting reviewer action"} />
                  <StatCard label="Approved" value={approvedItems.length} color={colors.success} bg="#f0fdf4" icon="✅" sub={`${Math.round((approvedItems.length / (items.length || 1)) * 100)}% approval rate`} />
                  <StatCard label="Rejected" value={rejectedItems.length} color={rejectedItems.length > 0 ? colors.danger : colors.success} bg={rejectedItems.length > 0 ? "#fef2f2" : "#f0fdf4"} icon={rejectedItems.length > 0 ? "❌" : "✅"} sub={rejectedItems.length > 0 ? "May need revision" : "None rejected"} />
                </div>

                {/* Two column: Content vs Design breakdown + Recent Activity */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "16px 20px" }}>
                    <h3 style={{ fontFamily: "DM Sans,sans-serif", fontWeight: 700, fontSize: 14, color: "#374151", marginBottom: 12 }}>By Type</h3>
                    {["Content", "Design"].map(type => {
                      const count = items.filter(i => i.fields?.Type_x0020_of_x0020_Approval === type).length;
                      const pctVal = items.length ? Math.round((count / items.length) * 100) : 0;
                      const badge = getApprovalBadge(type);
                      return (
                        <div key={type} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <Badge label={type} color={badge.color} bg={badge.bg} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${pctVal}%`, background: badge.color, borderRadius: 3, transition: "width 0.3s" }} />
                            </div>
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "DM Sans,sans-serif", minWidth: 40 }}>{count} ({pctVal}%)</span>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "16px 20px" }}>
                    <h3 style={{ fontFamily: "DM Sans,sans-serif", fontWeight: 700, fontSize: 14, color: "#374151", marginBottom: 12 }}>Recent Activity</h3>
                    {items.slice(0, 5).map(item => {
                      const f = item.fields || {};
                      const badge = getApprovalBadge(f.Type_x0020_of_x0020_Approval);
                      return (
                        <div key={item.id} onClick={() => setSelectedItem(item)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}>
                          <Badge label={f.Type_x0020_of_x0020_Approval || "—"} color={badge.color} bg={badge.bg} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", fontFamily: "DM Sans,sans-serif", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.Title}</span>
                          <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "DM Sans,sans-serif", whiteSpace: "nowrap" }}>{timeAgo(item.createdDateTime)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Pending approvals for approvers */}
                {isApprover && pendingApprovals.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ fontFamily: "DM Sans,sans-serif", fontWeight: 700, fontSize: 16, color: "#0f172a", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                      {Ic.inbox} Pending Your Review
                    </h3>
                    <RequestTable items={pendingApprovals} onView={setSelectedItem} emptyMsg="No pending approvals" />
                  </div>
                )}

                {/* Material Requests preview on dashboard */}
                {activeMatItems.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <h3 style={{ fontFamily: "DM Sans,sans-serif", fontWeight: 700, fontSize: 16, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                        {Ic.package} Active Material Requests
                      </h3>
                      <Btn small outline onClick={() => setTab("materials")} style={{ borderColor: "#e2e8f0", color: "#64748b" }}>
                        View All {Ic.arrowRight}
                      </Btn>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                      {activeMatItems.slice(0, 6).map(item => (
                        <MaterialRequestCard key={item.id} item={item} onClick={() => setTab("materials")} onCreateApproval={handleCreateApprovalFromMaterial} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* MATERIAL REQUESTS TAB */}
            {tab === "materials" && (
              <MaterialRequestsSection matItems={matItems} approvalItems={items} onCreateApproval={handleCreateApprovalFromMaterial} loading={loadingMat} />
            )}

            {/* ALL REQUESTS TAB */}
            {tab === "all" && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <h2 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>All Requests</h2>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {Ic.filter}
                    {["all", "content", "design", "pending", "approved", "rejected"].map(f => (
                      <button key={f} onClick={() => setFilter(f)}
                        style={{ padding: "5px 12px", border: "none", borderRadius: 20, background: filter === f ? colors.primary : "#f1f5f9", color: filter === f ? "#fff" : "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans,sans-serif", textTransform: "capitalize" }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
                <RequestTable items={filteredItems} onView={setSelectedItem} emptyMsg="No requests match this filter" />
              </>
            )}

            {/* APPROVALS TAB */}
            {tab === "approvals" && isApprover && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <h2 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 20, fontWeight: 800, color: "#0f172a" }}>Approval Queue</h2>
                  <p style={{ fontSize: 12, color: "#64748b", fontFamily: "DM Sans,sans-serif", marginTop: 2 }}>
                    {isContentApprover && isDesignApprover ? "Content & Design approvals" : isContentApprover ? "Content approvals" : "Design approvals"}
                  </p>
                </div>
                <RequestTable items={pendingApprovals} onView={setSelectedItem} emptyMsg="No pending approvals — you're all caught up!" />
              </>
            )}

            {/* MY SUBMISSIONS TAB */}
            {tab === "my" && (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <h2 style={{ fontFamily: "DM Sans,sans-serif", fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>My Submissions</h2>
                  <Btn onClick={() => setShowNewForm(true)}>{Ic.add} New Request</Btn>
                </div>
                <RequestTable items={myItems} onView={setSelectedItem} emptyMsg="You haven't submitted any requests yet" />
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showNewForm && <NewRequestForm onSubmit={handleSubmit} onClose={() => { setShowNewForm(false); setNewFormPrefill(null); }} loading={loadingAction} prefill={newFormPrefill} />}
      {selectedItem && <ItemDetail item={selectedItem} onClose={() => setSelectedItem(null)} onApprove={handleApprove} onReject={handleReject} isApprover={isApprover} loading={loadingAction} />}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}

// ── APP WRAPPER ─────────────────────────────────────────────────────────────
function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      (async () => {
        try {
          const token = await getToken(instance, accounts);
          const profile = await graphGet(token, "/me");
          setCurrentUser({
            name: profile.displayName,
            email: profile.mail || profile.userPrincipalName,
            jobTitle: profile.jobTitle,
            department: profile.department,
          });
        } catch (err) {
          console.error("Failed to load profile:", err);
        }
      })();
    }
  }, [isAuthenticated, accounts, instance]);

  if (!isAuthenticated || !accounts.length) return <LoginScreen />;
  if (!currentUser) {
    return (
      <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#1e3a5f 0%,#2d5a9e 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#fff", fontFamily: "DM Sans,sans-serif", fontSize: 16, fontWeight: 600 }}>Loading your profile...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ currentUser }}>
      <Dashboard />
    </AuthContext.Provider>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}
